import React from 'react';
import { Link } from 'react-router-dom';
import { Avatar } from 'ui/Avatar/Avatar';
import { BlockedEntity, BlockedEntityTab } from '../model/types';
import { formatDateTime, getManagementPath, initials } from './helpers';

interface Props {
  tab: BlockedEntityTab;
  items: BlockedEntity[];
  total: number;
  page: number;
  pageSize: number;
  selectedId: string;
  loading: boolean;
  error: string;
  onSelect: (id: string) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onRetry: () => void;
}

const getVisiblePages = (page: number, pages: number): number[] => {
  const start = Math.max(1, Math.min(page - 2, pages - 4));
  const end = Math.min(pages, Math.max(page + 2, 5));
  const result: number[] = [];
  for (let value = start; value <= end; value += 1) result.push(value);
  return result;
};

export const BlockedTable: React.FC<Props> = ({
  tab,
  items,
  total,
  page,
  pageSize,
  selectedId,
  loading,
  error,
  onSelect,
  onPageChange,
  onPageSizeChange,
  onRetry,
}) => {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const start = total ? (page - 1) * pageSize + 1 : 0;
  const end = Math.min(total, page * pageSize);

  return (
    <section className="blocked-table-card">
      <div className="blocked-table-card__heading">
        <div>
          <h2>
            {tab === 'users'
              ? 'Blocked users'
              : tab === 'groups'
                ? 'Blocked groups'
                : 'Blocked channels'}
          </h2>
          <p>
            {total.toLocaleString()} {total === 1 ? 'entity' : 'entities'} found
          </p>
        </div>
        <label className="blocked-table-card__page-size">
          Rows
          <select
            value={pageSize}
            onChange={(event) => onPageSizeChange(Number(event.target.value))}
          >
            {[10, 20, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="blocked-table-card__scroll">
        <table className="blocked-table">
          <thead>
            <tr>
              <th>
                {tab === 'users'
                  ? 'User'
                  : tab === 'groups'
                    ? 'Group'
                    : 'Channel'}
              </th>
              <th>Internal ID</th>
              <th>Blocking reason</th>
              <th>Date blocked</th>
              <th>Status</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {loading
              ? Array.from({ length: Math.min(pageSize, 8) }).map(
                  (_, index) => (
                    <tr
                      key={`skeleton-${index}`}
                      className="blocked-table__skeleton"
                    >
                      <td colSpan={6}>
                        <span />
                      </td>
                    </tr>
                  ),
                )
              : null}

            {!loading &&
              items.map((item) => (
                <tr
                  key={`${item.kind}-${item.id}`}
                  className={
                    item.id === selectedId
                      ? 'blocked-table__row--selected'
                      : undefined
                  }
                  onClick={() => onSelect(item.id)}
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onSelect(item.id);
                    }
                  }}
                >
                  <td>
                    <div className="blocked-table__entity">
                      <span className="blocked-table__avatar">
                        {item.avatarUrl ? (
                          <img src={item.avatarUrl} alt="" />
                        ) : item.avatarHash ? (
                          <Avatar hash={item.avatarHash} />
                        ) : (
                          initials(item.name)
                        )}
                      </span>
                      <span className="blocked-table__identity">
                        <strong>{item.name}</strong>
                        <small>{item.username || 'No public username'}</small>
                      </span>
                    </div>
                  </td>
                  <td>
                    <code>#{item.id}</code>
                  </td>
                  <td>
                    <span className="blocked-table__reason" title={item.reason}>
                      {item.reason}
                    </span>
                  </td>
                  <td>
                    <span className="blocked-table__date">
                      {formatDateTime(item.blockedAt)}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`blocked-status blocked-status--${
                        item.status === 'Blocked' ? 'blocked' : 'review'
                      }`}
                    >
                      <i />
                      {item.status}
                    </span>
                  </td>
                  <td>
                    <Link
                      className="blocked-table__open"
                      to={getManagementPath(item)}
                      onClick={(event) => event.stopPropagation()}
                      title="Open management page"
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M14 5h5v5M10 14 19 5M19 14v5H5V5h5" />
                      </svg>
                    </Link>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {!loading && error ? (
        <div className="blocked-table-card__state blocked-table-card__state--error">
          <strong>Unable to load data</strong>
          <span>{error}</span>
          <button type="button" onClick={onRetry}>
            Try again
          </button>
        </div>
      ) : null}

      {!loading && !error && !items.length ? (
        <div className="blocked-table-card__state">
          <span className="blocked-table-card__empty-icon">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 3 4 6v5c0 5 3.4 8.7 8 10 4.6-1.3 8-5 8-10V6l-8-3Zm-3 9h6" />
            </svg>
          </span>
          <strong>No blocked entities found</strong>
          <span>Change the search or filters and try again.</span>
        </div>
      ) : null}

      <footer className="blocked-table-card__footer">
        <span>
          Showing {start}–{end} of {total.toLocaleString()}
        </span>
        {pages > 1 ? (
          <nav className="blocked-pagination" aria-label="Pagination">
            <button
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => onPageChange(page - 1)}
              aria-label="Previous page"
            >
              ‹
            </button>
            {getVisiblePages(page, pages).map((value) => (
              <button
                key={value}
                type="button"
                disabled={loading}
                className={
                  value === page ? 'blocked-pagination__active' : undefined
                }
                onClick={() => onPageChange(value)}
                aria-current={value === page ? 'page' : undefined}
              >
                {value}
              </button>
            ))}
            <button
              type="button"
              disabled={page >= pages || loading}
              onClick={() => onPageChange(page + 1)}
              aria-label="Next page"
            >
              ›
            </button>
          </nav>
        ) : null}
      </footer>
    </section>
  );
};
